import * as _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import type { InjectedRouter } from 'react-router';

import { PublicErrorCode as ErrorCode } from '../../../../shared/errors/PublicErrorCode';
import { MULTI_ORG } from '../../../../shared/featureFlags/flagNames';
import { window } from '../../../globals';
import { useIsFeatureEnabled } from '../../../helpers/features/featureFlags';
import useAsyncStatus from '../../../hooks/useAsyncStatus';
import useInputValue from '../../../hooks/useInputValue';
import useOnMount from '../../../hooks/useOnMount';
import { get, post, put, remove } from '../../../redux/api';
import { hardRedirectTo } from '../../../utils';
import type { AsyncStatus, OrganizationAccessRequest } from '../../../types';
import type { TextInputOnChange } from '../../generic/Forms/InputBase';

import { createOrganization } from './helpers';

type InputState = {
  value: string;
  onChange: TextInputOnChange;
};

type RequestOrgAccessModal = {
  orgTitle: string;
  isShown: boolean;
  onHide: () => void;
  onRequestAccess: () => void;
};

type AccessRequest = {
  state: 'pending' | 'granted' | null;
  orgTitle?: string;
};

type UseOrgSetup = {
  isOnPrem: boolean;
  isSubmitDisabled: boolean;
  status: AsyncStatus;
  email: InputState;
  orgTitle: InputState;
  joinableOrgs: JoinableOrg[];
  orgNameInputRef: RefObject<HTMLInputElement>;
  orgToJoin: number | null;
  setOrgToJoin: (orgToJoin: number | null) => void;
  showCreateOrJoinSelector: boolean;
  showOrganizationPicker: boolean;
  toggleShowOrganizationPicker: (showOrganizationPicker: boolean) => void;
  requestOrgAccessModal: RequestOrgAccessModal;
  accessRequest: AccessRequest;
  onCancelAccessRequest: () => Promise<void>;
  onSubmit: () => Promise<void>;
};

//  Minimal org, returned from /api/registration-organizations
type JoinableOrg = {
  id: number;
  title: string;
};

/**
 * Small hook to focus the orgNameInput
 */
function useFocusOrgNameField(showOrganizationPicker: boolean): RefObject<HTMLInputElement> {
  const orgNameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!showOrganizationPicker && orgNameInputRef.current) {
      orgNameInputRef.current.focus();
    }
  }, [orgNameInputRef.current, showOrganizationPicker]);

  return orgNameInputRef;
}

/**
 * Make the request to put the user into an organization.
 * This is only available for on-prem customers who have window.enableSignupOrganizationPicker enabled.
 *
 * @param {number} orgToJoin - ID of the org to be joined.
 */
async function joinOrganization(orgToJoin: number) {
  return put('/api/user/join-organization', { organizationId: orgToJoin });
}

/**
 * Cancel your access request.
 */
async function cancelAccessRequest() {
  await remove('/account/access_request');
  window.user.accessRequests = [];
}

/**
 * Checks the window object for any OrganizationAccessRequests for the user.
 *
 * @returns {AccessRequest} Status and orgTitle for the access request
 */
function checkForAccessRequests(): AccessRequest {
  const accessRequests = window.user?.accessRequests;
  //  Check if `window.user.accessRequests` is empty
  if (accessRequests && !_.isEmpty(accessRequests)) {
    const firstAccessRequest = _.head(accessRequests) as OrganizationAccessRequest;
    const orgTitle = firstAccessRequest?.organization.title ?? 'Organization';
    if (firstAccessRequest?.fulfilled) {
      return { state: 'granted', orgTitle };
    }
    return { state: 'pending', orgTitle };
  }
  return { state: null };
}

/**
 * Hook for managing state for the Org Setup step in Onboarding.
 *
 * For cloud users who weren't invited/LDAP'd into an org, they'll need to create a new org.
 * For on prem users who are not in an org, they will be shown this so they can either
 * select the org they want to join, or create a new one.
 */
const useOrgSetup = (router: InjectedRouter): UseOrgSetup => {
  const isOnPrem = window.on_prem;
  // Under MULTI_ORG the request-access / approval workflow is no longer supported (neither cloud nor on-prem). The
  // on-prem "select an org to join" dropdown stays; requesting access to an org and awaiting approval is dropped.
  const isMultiOrg = useIsFeatureEnabled(MULTI_ORG);
  const [email, onEmailChange] = useInputValue(window.user?.email ?? '');
  const [orgTitle, onOrgTitleChange] = useInputValue('');
  const [joinableOrgs, setJoinableOrgs] = useState<JoinableOrg[]>([]);
  const [orgToJoin, setOrgToJoin] = useState<number | null>(null);
  const [showOrganizationPicker, toggleShowOrganizationPicker] = useState(false);
  const [showRequestOrgAccessModal, setShowRequestOrgAccessModal] = useState(false);
  const {
    status,
    onPending,
    onSuccess,
    onError,
  } = useAsyncStatus();
  const orgNameInputRef = useFocusOrgNameField(showOrganizationPicker);

  //  For on-prems, if there are more than one org to join, give them the option.
  const showCreateOrJoinSelector = isOnPrem && joinableOrgs.length > 0;
  const isSubmitDisabled = showOrganizationPicker
    ? status.loading || !orgToJoin
    : status.loading || email.trim().length === 0;

  const onHideRequestOrgAccessModal = () => setShowRequestOrgAccessModal(false);
  const onShowRequestOrgAccessModal = () => setShowRequestOrgAccessModal(true);

  /**
   * Fetches a list of organizations which can be joined.
   * This is only used for on-prem customers where enableSignupOrganizationPicker is true,
   * in cases where it's not the request is skipped.
   */
  async function getOrganizations() {
    if (isOnPrem && window.enableSignupOrganizationPicker) {
      onPending();
      const { data } = await get<JoinableOrg[]>('/api/registration-organizations');
      setJoinableOrgs(data || []);
      //  If there is at least one org, let's select the first one by default.
      //  We want to nudge users towards joining existing orgs instead of creating new ones
      const firstOrg = _.head(data || []);
      if (firstOrg) {
        setOrgToJoin(firstOrg.id);
        toggleShowOrganizationPicker(true);
      } else {
        toggleShowOrganizationPicker(false);
      }
      onSuccess();
    }
    return [];
  }
  //  Fetch the list of joinable orgs for the org picker, if enabled.
  useOnMount(getOrganizations);

  //  Handle request for creating a brand spanking new org!
  async function onCreateNewOrganization() {
    await createOrganization(orgTitle.trim() || email, email);
    onSuccess();
    //  Go to project import page now that we in and have a default policy!
    router.push('/projects/import');
  }

  //  Handle request for accessing an org.
  async function onJoinOrganization() {
    if (orgToJoin) {
      try {
        await joinOrganization(orgToJoin);
        onSuccess();
        // The user was added to an organization. Hard redirect to the app so that the global state gets reset.
        hardRedirectTo('/');
      } catch (error) {
        onError(error);
      }
    }
  }

  //  Handle removing access requests.
  async function onCancelAccessRequest() {
    onPending();
    try {
      await cancelAccessRequest();
      onSuccess();
    } catch (error) {
      onError(error);
    }
  }

  //  Handle request for creating an org access request.
  async function onRequestOrgAccess() {
    try {
      const { data: accessRequest } = await post<{}, OrganizationAccessRequest>(`/organizations/${orgTitle}/request_access`, {}); // Empty body is intentional.
      window.user.accessRequests = accessRequest
        ? [accessRequest]
        : [];
      onSuccess();
      onHideRequestOrgAccessModal();
    } catch (error) {
      onError(error);
    }
  }

  //  Handle submit
  const onSubmit = async () => {
    if (isSubmitDisabled) {
      return;
    }
    onPending();

    try {
      //  Handle cases where the user is attempting to join an existing org.
      if (showOrganizationPicker) {
        await onJoinOrganization();
      } else {
        await onCreateNewOrganization();
      }
    } catch (error) {
      //  Pre-MULTI_ORG on-prem: a name collision offered to request access to the existing org. Under MULTI_ORG
      //  that workflow is gone, so the collision just surfaces as an error.
      if (!isMultiOrg && isOnPrem && error.data.code === ErrorCode.orgNameAlreadyExists) {
        onShowRequestOrgAccessModal();
        onSuccess();
        return;
      }
      //  In rare cases where the user has already been added to the org, just refresh and the Onboarding step is bypassed.
      if (error.data.code === ErrorCode.userAlreadyInOrg) {
        onSuccess();
        window.location.reload();
      }
      onError(error);
    }
  };

  return {
    isOnPrem,
    isSubmitDisabled,
    status,
    email: {
      value: email,
      onChange: onEmailChange,
    },
    orgTitle: {
      value: orgTitle,
      onChange: onOrgTitleChange,
    },
    joinableOrgs,
    orgNameInputRef,
    orgToJoin,
    setOrgToJoin,
    showCreateOrJoinSelector,
    showOrganizationPicker,
    toggleShowOrganizationPicker,
    requestOrgAccessModal: {
      orgTitle,
      isShown: showRequestOrgAccessModal,
      onHide: onHideRequestOrgAccessModal,
      onRequestAccess: onRequestOrgAccess,
    },
    accessRequest: isMultiOrg ? { state: null } : checkForAccessRequests(),
    onCancelAccessRequest,
    onSubmit,
  };
};

export default useOrgSetup;
